import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type ActorEvent, ARRANGEMENT, type Arrangement, type RecordLikeProtocol, type SizeConfigure } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { getRecordPoolKey, useAtomData, useFlowLayoutContext } from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import { useStore } from 'jotai'
import React, { forwardRef, memo, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { calculateNewSortValue, getEffectiveSortValue } from '../shared/sortUtils'
import { Item } from './Item'
import { geVerticalWidthSize, setDeadCopyNode } from './utils'

const Root = styled.div<{ gap: number }>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: flex-start;
    align-items: flex-start;
    width: 100%;
    gap: ${({ gap }) => `${gap}px`};
`

const WrapItem = styled.div<{ isFitArrangement: boolean; itemWidth: number }>`
    flex: 0 0 auto;
    width: ${({ isFitArrangement, itemWidth }) => (isFitArrangement ? 'fit-content' : `${itemWidth}px`)};
    max-width: 100%;
    min-width: 0;
`

interface Props extends React.ComponentPropsWithoutRef<'div'> {
    node: FlowLayoutNode
    anchorId?: string
    title: string
    itemWidth: number
    gap: number
    arrangement: Arrangement
    readonly?: boolean
    enableDrag?: boolean
    sortId?: string
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onUpdateRecord?: (recordId: string, content: RecordLikeProtocol['content']) => Promise<RecordLikeProtocol>
}

interface SortableItemProps {
    id: string
    disabled?: boolean
    itemWidth: number
    isFitArrangement: boolean
    children: React.ReactNode
}

const SortableItem = memo<SortableItemProps>(({ id, disabled, itemWidth, isFitArrangement, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })

    return (
        <WrapItem
            ref={setNodeRef}
            itemWidth={itemWidth}
            isFitArrangement={isFitArrangement}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
                cursor: disabled ? undefined : isDragging ? 'grabbing' : 'grab'
            }}
            {...attributes}
            {...listeners}
        >
            {children}
        </WrapItem>
    )
})

function buildHorizontalWrapItemData(params: {
    node: FlowLayoutNode
    blockId: string
    recordId: string
    index: number
    readonly?: boolean
    widthSize: SizeConfigure
}): FlowLayoutNode {
    const { node, blockId, recordId, index, readonly, widthSize } = params

    return {
        id: `${blockId}/${recordId}`,
        scope: node.scope,
        type: 'container',
        disabled: readonly || index !== 0,
        virtual: true,
        data: node.data && {
            design: node.data.design,
            size: {
                width: widthSize,
                height: { size: 'auto' }
            },
            layout: node.data.layout,
            position: node.data.position
        },
        children: readonly ? node.children : setDeadCopyNode(node.children || [], index === 0, blockId)
    }
}

export const HorizontalWrapView = memo(
    forwardRef<HTMLDivElement, Props>((props, ref) => {
        const { node, anchorId, title, itemWidth, gap, arrangement, readonly, enableDrag, sortId, onRecordClick, onUpdateRecord, style } = props
        const innerRef = useRef<HTMLDivElement | null>(null)
        const { blockData, recordPoints, recordPoolAtom, dataSource } = useViewBlockContext()
        const { id: blockId } = blockData
        const { id: dsId, schema } = dataSource
        const store = useStore()
        const isFitArrangement = arrangement === ARRANGEMENT.fit
        const widthSize = useMemo(() => geVerticalWidthSize(isFitArrangement, itemWidth), [isFitArrangement, itemWidth])

        const sortedRecordPoints = useAtomData(
            recordPoolAtom,
            useCallback(
                pool => {
                    if (!enableDrag) {
                        return recordPoints
                    }

                    return [...recordPoints].sort((a, b) => {
                        const recordA = pool.get(getRecordPoolKey(dsId, a.id))
                        const recordB = pool.get(getRecordPoolKey(dsId, b.id))
                        if (!recordA || !recordB) {
                            return 0
                        }
                        return getEffectiveSortValue(recordA, sortId) - getEffectiveSortValue(recordB, sortId)
                    })
                },
                [enableDrag, recordPoints, dsId, sortId]
            )
        )

        const recordIds = useMemo(() => sortedRecordPoints.map(recordPoint => recordPoint.id), [sortedRecordPoints])
        const items = useMemo(
            () =>
                sortedRecordPoints.map((recordPoint, index) => ({
                    recordId: recordPoint.id,
                    index,
                    data: buildHorizontalWrapItemData({ node, blockId, recordId: recordPoint.id, index, readonly, widthSize })
                })),
            [blockId, node, readonly, sortedRecordPoints, widthSize]
        )

        const sensors = useSensors(
            useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
            useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
        )

        const handleDragEnd = useCallback(
            (event: DragEndEvent) => {
                const { active, over } = event
                if (!over || active.id === over.id || !enableDrag || !sortId || !onUpdateRecord) {
                    return
                }

                const draggedId = String(active.id)
                const activeIndex = recordIds.indexOf(draggedId)
                const overIndex = recordIds.indexOf(String(over.id))
                if (activeIndex === -1 || overIndex === -1) {
                    return
                }

                const reordered = arrayMove(recordIds, activeIndex, overIndex)
                const pool = store.get(recordPoolAtom)
                const siblings = reordered
                    .filter(recordId => recordId !== draggedId)
                    .map(recordId => {
                        const record = pool.get(getRecordPoolKey(dsId, recordId))
                        return record ? { id: recordId, record } : null
                    })
                    .filter((item): item is { id: string; record: RecordLikeProtocol } => Boolean(item))
                const insertIndex = reordered.indexOf(draggedId)
                const siblingsBeforeDragged = reordered.slice(0, insertIndex).filter(recordId => recordId !== draggedId).length
                const newSortValue = calculateNewSortValue(siblings, siblingsBeforeDragged, sortId)

                onUpdateRecord(draggedId, { [sortId]: { ...schema[sortId], value: newSortValue } })
            },
            [dsId, enableDrag, onUpdateRecord, recordIds, recordPoolAtom, schema, sortId, store]
        )

        const { selectedNodes } = useFlowLayoutContext()
        const isBlockSelected = useMemo(
            () => selectedNodes?.some(item => item.scope === node.scope && item.id === node.id),
            [node.id, node.scope, selectedNodes]
        )
        const dragActive = readonly ? true : isBlockSelected

        const renderItems = (
            <Root ref={mergeRefs([innerRef, ref])} id={anchorId} gap={gap} style={style}>
                {items.map(({ recordId, index, data }) => {
                    const item = (
                        <Item
                            data-record-id={recordId}
                            recordId={recordId}
                            recordIndexPath={String(index)}
                            readonly={readonly}
                            data={data}
                            title={title}
                            onRecordClick={onRecordClick}
                        />
                    )

                    if (enableDrag) {
                        return (
                            <SortableItem
                                key={recordId}
                                id={recordId}
                                disabled={!dragActive}
                                itemWidth={itemWidth}
                                isFitArrangement={isFitArrangement}
                            >
                                {item}
                            </SortableItem>
                        )
                    }

                    return (
                        <WrapItem key={recordId} itemWidth={itemWidth} isFitArrangement={isFitArrangement}>
                            {item}
                        </WrapItem>
                    )
                })}
            </Root>
        )

        if (!enableDrag) {
            return renderItems
        }

        return (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={recordIds} disabled={!dragActive} strategy={rectSortingStrategy}>
                    {renderItems}
                </SortableContext>
            </DndContext>
        )
    })
)